import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AsyncBoundary } from '@kollez/design-system';
import { apiGet } from '@/lib/api';
import { initialTableParams, toQueryString, type Paginated, type TableQueryParams } from '@/lib/tableQuery';
import { Pagination, SortHeader, TableSearch, ResetButton } from '@/components/DataTable';

interface AISHERow {
  _id: string;
  aisheCode: string;
  name: string;
  city?: string;
  state?: string;
  type?: string;
  university?: string;
  affiliatedUniversity?: string;
}

/**
 * /platform/colleges — national AISHE master (read-only for platform).
 * Server-side search across name/code/city, plus state + type filters, sortable columns.
 */
const initial: TableQueryParams = initialTableParams({ sort: 'name', order: 'asc' });

const TYPE_OPTIONS = [
  ['', 'Any type'],
  ['Engineering', 'Engineering'],
  ['Pharmacy', 'Pharmacy'],
  ['Management', 'Management'],
  ['Degree', 'Degree'],
  ['Polytechnic', 'Polytechnic'],
] as const;

export function PlatformCollegesPage() {
  const [params, setParams] = useState<TableQueryParams>(initial);
  const [qDraft, setQDraft] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setParams((p) => ({ ...p, q: qDraft || undefined, page: 1 })), 300);
    return () => clearTimeout(t);
  }, [qDraft]);

  const query = useQuery({
    queryKey: ['platform', 'colleges', params],
    queryFn: () => apiGet<Paginated<AISHERow>>(`/platform/colleges?${toQueryString(params)}`),
    placeholderData: (prev) => prev,
  });

  const onSort = (field: string) => setParams((p) => ({
    ...p,
    sort: field,
    order: p.sort === field && p.order === 'asc' ? 'desc' : 'asc',
    page: 1,
  }));

  const reset = () => {
    setQDraft('');
    setParams(initial);
  };
  const filtersDirty = !!(params.q || params.type || params.state || params.sort !== initial.sort || params.order !== initial.order);

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Colleges (AISHE master)</h1>
          <p className="subhead">National reference list. Tenant sign-ups match here so NIRF and AICTE data line up.</p>
        </div>
      </div>

      <div className="card">
        <div className="filterbar">
          <TableSearch value={qDraft} onChange={setQDraft} placeholder="AISHE code, name or city" />
          <select className="select" value={String(params.type ?? '')} onChange={(e) => setParams((p) => ({ ...p, type: e.target.value || undefined, page: 1 }))}>
            {TYPE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input
            className="input"
            placeholder="State"
            style={{ maxWidth: 140 }}
            value={String(params.state ?? '')}
            onChange={(e) => setParams((p) => ({ ...p, state: e.target.value || undefined, page: 1 }))}
          />
          <ResetButton onReset={reset} disabled={!filtersDirty} />
          <span className="fb-count">
            {query.data ? `${query.data.total.toLocaleString('en-IN')} total` : ''}
          </span>
        </div>

        <AsyncBoundary kind="table" inCard loading={query.isLoading} error={query.error} empty={!!(query.data && query.data.total === 0)} emptyTitle="No colleges match">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <SortHeader label="Code" field="aisheCode" currentSort={params.sort ?? null} currentOrder={params.order ?? 'asc'} onSort={onSort} />
                  <SortHeader label="Name" field="name" currentSort={params.sort ?? null} currentOrder={params.order ?? 'asc'} onSort={onSort} />
                  <SortHeader label="City" field="city" currentSort={params.sort ?? null} currentOrder={params.order ?? 'asc'} onSort={onSort} className="hide-mobile" />
                  <SortHeader label="State" field="state" currentSort={params.sort ?? null} currentOrder={params.order ?? 'asc'} onSort={onSort} className="hide-mobile" />
                  <th className="hide-mobile">Type</th>
                  <th className="hide-mobile">Affiliated to</th>
                </tr>
              </thead>
              <tbody>
                {query.data?.rows.map((r) => (
                  <tr key={r._id} style={{ cursor: 'pointer' }} onClick={() => { window.location.href = `/platform/colleges/${r._id}`; }}>
                    <td className="cell-title"><Link to={`/platform/colleges/${r._id}`} onClick={(e) => e.stopPropagation()}>{r.aisheCode}</Link></td>
                    <td>{r.name}</td>
                    <td className="hide-mobile cell-sub">{r.city ?? '—'}</td>
                    <td className="hide-mobile cell-sub">{r.state ?? '—'}</td>
                    <td className="hide-mobile">{r.type ?? '—'}</td>
                    <td className="hide-mobile cell-sub">{r.university ?? r.affiliatedUniversity ?? 'Autonomous'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {query.data && (
            <Pagination
              page={query.data.page}
              totalPages={query.data.totalPages}
              total={query.data.total}
              limit={query.data.limit}
              onPage={(page) => setParams((p) => ({ ...p, page }))}
              onLimit={(limit) => setParams((p) => ({ ...p, limit, page: 1 }))}
            />
          )}
        </AsyncBoundary>
      </div>
    </div>
  );
}
